<!--
	This component is a generic layout for a list of sections each containing a list of items, e.g.
	a menu with products divided into sections, or a grocery aisle divided into sub-aisles.  Each
	section gets rendered as a swimlane with a "See all" which, when clicked, shows a second view: a
	grid showing all the items in that section.
-->
<script lang="ts" context="module">
	export const KEY_SECTION = "section"; // TODO: Good name (being used in styleguide...)? + find better home for it

	export interface SectionedList<List, Section, SectionID, Item> {
		/** How this component accesses the sections in the list */
		getSections(list?: List): Section[] | undefined;
		/** How the component fetches one section of the list by its ID */
		getSection(id: SectionID, list: List | undefined): Section | undefined;
		/** How the component finds the identifier value of a section */
		getSectionId(section: Section): SectionID;
		/** How the component finds the title of the section */
		getSectionTitle(section: Section): string;
		/** How the component finds/creates the URL to see all the items in a section as a grid.  */
		getSectionUrl(section: Section): string;
		/** How the component gets the items in a given section */
		getItems(section: Section): Item[];
		/** How the component finds the identifier value of an item in a section */
		getItemId(item: Item): string | number;

	}
</script>
<script lang="ts" generics="List, Section, SectionID, Item">
	import type { Cache } from "$lib/serv/core/cache";
	import type { Snippet } from "svelte";
	
	import Grid from "$lib/ui/layout/Grid.svelte";
	import CarouselV2 from "./CarouselV2.svelte";
  import { slugify } from "$lib/util/format";

	type Props = {
		cache: Cache<List>;
		listWrapper: SectionedList<List, Section, SectionID, Item>;
		showSeeAllBtn?: boolean;
		sectionId?: SectionID | null;
		sectionChildren?: Snippet<[{
			item: Item;
			sectionId: SectionID;
		}]>;
	}

	let {
		cache,
		listWrapper,
		showSeeAllBtn = true,
		sectionId = null,
		sectionChildren,
	}: Props = $props();

	const {
		getSections,
		getSection,
		getSectionId,
		getSectionTitle,
		getSectionUrl,
		getItems,
		getItemId,
	} = listWrapper;

</script>

{#if !sectionId}
	{@const sections = getSections(cache.payload)}
	{#if sections}
		{#each sections as section}
			{@const id = `${getSectionId(section)}`} <!-- TODO: Should parent component decide what the # should be? -->
			{@const name = getSectionTitle(section)}
			{@const url = getSectionUrl(section)}
			<CarouselV2 cache={{ ...cache, payload: section }} key={(s) => getItems(section)}>
				{#snippet title()}
					<div class="flex mobile flex-nowrap title">
						<h2 class="headline-m bold">{name}</h2>
						<a href={url} class="button transparent label-xl bold underlined">See all</a>
					</div>
				{/snippet}
				{#snippet children({ item })}
					{@render sectionChildren?.({ item, sectionId: getSectionId(section) })}
				{/snippet}
			</CarouselV2>
		{/each}
	{/if}
{:else}  <!-- show a specific section -->
	{@const section = getSection(sectionId, cache.payload)}
	{#if section}
		{@const items = getItems(section)} <!-- TODO: Do we need to consider duplicate items? -->
		{@const title = getSectionTitle(section)}
		<h4 id={slugify(title)} class="headline-m bold ellipses">{title}</h4>
		<Grid cache={{ payload: items }} type="aisle" key={(p) => p} let:item>
			{@render sectionChildren?.({ item, sectionId: getSectionId(section) })}
		</Grid>
	{:else}
		<!-- TODO: Section not found error state -->
	{/if}
{/if}